import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AI_CONTROL_CPS } from "@/constants/testIds";
import HintIcon from "@/components/HintIcon";
import { toast } from "sonner";
import {
  Radar, Play, RotateCcw, ShieldCheck, AlertTriangle, RefreshCw, Info,
} from "lucide-react";

/** Admin panel for CPS V2 shadow-mode monitoring and mode control.
 *  Reached from /ai-control (admin only). Never auto-flips to V2 — every
 *  mode change is an explicit button click that hits an audit-logged endpoint.
 */
export default function ShadowStatusPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // Two-click confirm — arms one of: "shadow" | "v2" | "legacy" | ""
  // Native confirm() is silently blocked by the preview iframe.
  const [confirmingAction, setConfirmingAction] = useState("");

  const armConfirm = (name) => {
    setConfirmingAction(name);
    setTimeout(() => setConfirmingAction((cur) => (cur === name ? "" : cur)), 5000);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/scoring-config/shadow-status");
      setStatus(data);
    } catch (e) {
      toast.error("Failed to load shadow status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const activateShadow = async () => {
    if (confirmingAction !== "shadow") { armConfirm("shadow"); return; }
    setConfirmingAction("");
    setBusy(true);
    try {
      await api.post("/scoring-config/activate-shadow");
      toast.success("Shadow mode activated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  const activateV2 = async () => {
    if (confirmingAction !== "v2") { armConfirm("v2"); return; }
    setConfirmingAction("");
    setBusy(true);
    try {
      await api.post("/scoring-config/activate-v2");
      toast.success("V2 is now the production score");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Threshold not met — see reasons");
    } finally { setBusy(false); }
  };
  const revert = async () => {
    if (confirmingAction !== "legacy") { armConfirm("legacy"); return; }
    setConfirmingAction("");
    setBusy(true);
    try {
      await api.post("/scoring-config/revert-to-legacy");
      toast.success("Reverted to legacy");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (loading && !status) {
    return <div className="text-sm text-slate-500 p-4">Loading shadow status…</div>;
  }
  if (!status) return null;

  const mode = status.current_mode;
  const modeTone =
    mode === "shadow" ? "bg-indigo-100 text-indigo-700 border-indigo-300"
    : mode === "v2" ? "bg-emerald-100 text-emerald-700 border-emerald-300"
    : "bg-slate-100 text-slate-700 border-slate-300";
  const ready = status.ready_for_human_review;

  return (
    <div
      data-testid={AI_CONTROL_CPS.root}
      className="border border-slate-200 rounded-lg bg-white p-5"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-1.5">
            <Radar className="h-5 w-5 text-indigo-600" />
            <h2 className="font-heading text-lg font-bold text-slate-900">
              CPS V2 — Shadow Monitoring
            </h2>
            <HintIcon
              size="sm"
              side="bottom"
              testId="shadow-monitoring-hint"
              hint="CPS V2 is running silently alongside the current scoring system so we can test its results before making it active. Nothing changes automatically."
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            data-testid={AI_CONTROL_CPS.modeBadge}
            className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded border ${modeTone}`}
          >
            Mode: {mode}
          </span>
          <span className="text-[10px] font-mono text-slate-500">v{status.current_config_version}</span>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 hover:text-indigo-700 disabled:opacity-60"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Metrics grid */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border border-slate-200 rounded-md p-3">
          <div className="flex items-center gap-1">
            <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">Testing Days</div>
            <HintIcon
              size="sm"
              side="bottom"
              testId="days-elapsed-hint"
              hint="Minimum 5 days of shadow testing is required before CPS V2 can be considered for activation. Today is Day 0."
            />
          </div>
          <div className="font-heading font-extrabold text-2xl tabular text-slate-900" data-testid={AI_CONTROL_CPS.daysElapsed}>
            {status.days_elapsed}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">≥ {status.min_days} required</div>
        </div>
        <div className="border border-slate-200 rounded-md p-3">
          <div className="flex items-center gap-1">
            <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">Full-Fidelity Events</div>
            <HintIcon
              size="sm"
              side="bottom"
              testId="full-fidelity-hint"
              hint="A complete CPS V2 score calculated with enough user evidence. At least 100 Full-Fidelity score snapshots are required before human review."
            />
          </div>
          <div className="font-heading font-extrabold text-2xl tabular text-indigo-700" data-testid={AI_CONTROL_CPS.fullFidelityCount}>
            {status.full_fidelity_snapshots}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">≥ {status.min_events} required</div>
        </div>
        <div className="border border-slate-200 rounded-md p-3">
          <div className="flex items-center gap-1">
            <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">Provisional</div>
            <HintIcon
              size="sm"
              side="bottom"
              testId="provisional-hint"
              hint="A CPS V2 score calculated without sufficient AI conversation data. It uses the other available CPS V2 data, such as engagement, momentum, app recency, and trial lifecycle. The score is less reliable and does not count toward the Full-Fidelity requirement."
            />
          </div>
          <div className="font-heading font-extrabold text-2xl tabular text-amber-600" data-testid={AI_CONTROL_CPS.provisionalCount}>
            {status.provisional_snapshots}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">excluded from threshold</div>
        </div>
        <div className="border border-slate-200 rounded-md p-3">
          <div className="flex items-center gap-1">
            <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">Unique Users</div>
            <HintIcon
              size="sm"
              side="bottom"
              testId="unique-users-hint"
              hint="The number of different users evaluated by CPS V2 during shadow testing. One user may generate multiple Full-Fidelity or Provisional score snapshots as their conversation and activity change."
            />
          </div>
          <div className="font-heading font-extrabold text-2xl tabular text-slate-900" data-testid={AI_CONTROL_CPS.uniqueUsers}>
            {status.unique_users_scored}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">total events: {status.total_snapshots}</div>
        </div>
      </div>

      {/* Ready-for-review banner */}
      <div
        className={`mt-4 flex items-start gap-2 border rounded-md px-3 py-2 ${
          ready ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"
        }`}
      >
        {ready ? (
          <ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
        ) : (
          <Info className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
        )}
        <div className="text-xs">
          <div className="font-semibold" data-testid={AI_CONTROL_CPS.readyBadge}>
            {ready ? "Ready for human review" : "Not yet ready for human review"}
          </div>
          <div className="text-slate-600 mt-0.5">
            {status.human_review_note}
          </div>
          {status.reasons_not_ready?.length > 0 && (
            <ul className="mt-1 list-disc list-inside text-slate-600">
              {status.reasons_not_ready.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          )}
        </div>
      </div>

      {/* Timestamps */}
      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-[11px] text-slate-600">
        <div>
          <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">Shadow Start</div>
          <div className="font-mono">{status.shadow_start ? new Date(status.shadow_start).toLocaleString() : "—"}</div>
        </div>
        <div>
          <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">Latest Snapshot</div>
          <div className="font-mono">{status.latest_snapshot_at ? new Date(status.latest_snapshot_at).toLocaleString() : "—"}</div>
        </div>
        <div>
          <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">Activated By / At</div>
          <div className="font-mono">
            {status.activated_by ? `${status.activated_by.slice(0, 8)}…` : "—"} · {status.activated_at ? new Date(status.activated_at).toLocaleString() : "—"}
          </div>
        </div>
      </div>

      {/* Mode switches */}
      <div className="mt-5 pt-4 border-t border-slate-200 flex flex-wrap gap-2">
        {mode !== "shadow" && (
          <button
            data-testid={AI_CONTROL_CPS.activateShadowBtn}
            onClick={activateShadow}
            disabled={busy}
            title={confirmingAction === "shadow" ? "Click again to confirm" : "Run CPS V2 alongside legacy — no live impact"}
            className={`inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-md border transition-colors duration-150 disabled:opacity-60 ${confirmingAction === "shadow" ? "border-indigo-600 bg-indigo-600 text-white" : "border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50"}`}
          >
            <Play className="h-3.5 w-3.5" /> {confirmingAction === "shadow" ? "Click to confirm — activate shadow" : "Activate Shadow"}
          </button>
        )}
        {mode !== "v2" && (
          <button
            data-testid={AI_CONTROL_CPS.activateV2Btn}
            onClick={activateV2}
            disabled={busy || !ready}
            title={!ready ? "Not ready for human review yet" : (confirmingAction === "v2" ? "Click again to confirm — V2 goes LIVE" : "Flip V2 to production")}
            className={`inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-md border transition-colors duration-150 disabled:opacity-40 ${confirmingAction === "v2" ? "border-emerald-600 bg-emerald-600 text-white" : "border-emerald-300 text-emerald-700 bg-white hover:bg-emerald-50"}`}
          >
            <ShieldCheck className="h-3.5 w-3.5" /> {confirmingAction === "v2" ? "Click to confirm — go live with V2" : "Activate V2 (production)"}
          </button>
        )}
        {mode !== "legacy" && (
          <button
            data-testid={AI_CONTROL_CPS.revertLegacyBtn}
            onClick={revert}
            disabled={busy}
            title={confirmingAction === "legacy" ? "Click again to confirm — V2 stops scoring" : "Revert to legacy scoring"}
            className={`inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-md border transition-colors duration-150 disabled:opacity-60 ${confirmingAction === "legacy" ? "border-red-600 bg-red-600 text-white" : "border-red-300 text-red-700 bg-white hover:bg-red-50"}`}
          >
            <RotateCcw className="h-3.5 w-3.5" /> {confirmingAction === "legacy" ? "Click to confirm — revert" : "Revert to Legacy"}
          </button>
        )}
      </div>
    </div>
  );
}
